import { useEffect } from "react";

/**
 * Locks <body> scroll while `active` is true. Unlocks on cleanup.
 *
 * iOS-safe: uses `overflow: hidden` + `touchAction: none` together.
 * `overflow: hidden` alone does NOT stop the iOS Safari rubber-band
 * (the body is technically not scrolling, but the page can still pull
 * down past its bounds with a finger drag); `touchAction: none` on
 * the body prevents that gesture from being interpreted as scroll.
 *
 * Reference-counted via a module-level counter so multiple concurrent
 * locks compose correctly. Closing one modal while another is still
 * open keeps the lock; only the last release restores the original
 * body styles. The original values are captured on the FIRST lock so
 * we restore exactly what the page had before any modal opened (and
 * not e.g. the styles set by a previous lock that's still on the
 * stack).
 *
 * No-op when `active` is false — safe to call unconditionally inside
 * a component and have the lock track an `open` prop.
 *
 * Used by every full-overlay modal and bottom sheet:
 *   AchievementShowcase / ArchivistLedger / SettingsPanel /
 *   RegionMarker (mobile sheet only) / HowToPlayTutorial /
 *   ChronicleProgressDialog / Play.tsx in-game dialogs.
 */
let lockCount = 0;
let originalOverflow = "";
let originalTouchAction = "";

export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    if (lockCount === 0) {
      originalOverflow = document.body.style.overflow;
      originalTouchAction = document.body.style.touchAction;
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    }
    lockCount++;
    return () => {
      lockCount--;
      if (lockCount === 0) {
        document.body.style.overflow = originalOverflow;
        document.body.style.touchAction = originalTouchAction;
      }
    };
  }, [active]);
}
